import { AlertTriangle, Info, XCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'
import { isStageBody, useEditorStore, type EditorState } from '@/stores/editorStore'
import type { Lint } from '@/schema'

import { useKindUi } from './KindPicker'
import { domainOfFeature, featuresOf, prettyId, useVocab } from './vocab'

/**
 * The lint list (§3.2, §4.3.7).
 *
 * Lints are already plain English on the wire — `message` is required and `fix_hint` is the
 * action — so this component adds an icon for the severity, a *show me* button that selects the
 * feature the lint points at, and a direct fix for the kind problems: a thing whose kind is not
 * in the list can be given one (or the kind added), and a kind that does nothing yet can be told
 * what it does. It never shows a code to the operator; the code is in the `title` for a bug report.
 *
 * Problems do **not** stop a save. Errors stop the picture being used in a story, which is what
 * the sentence at the bottom says.
 */

export interface LintListProps {
  className?: string
  /** Simple fix mode shows sentences only — no codes, no buttons. */
  plain?: boolean
}

const ICON = {
  error: XCircle,
  warn: AlertTriangle,
  info: Info,
} as const

const TONE = {
  error: 'text-danger',
  warn: 'text-warn',
  info: 'text-info',
} as const

const EMPTY: Lint[] = []

/** The latest checks: the live-lint slice when the editor has one, else the saved document's. */
function selectLints(s: EditorState): Lint[] {
  const derived = (s as EditorState & { derived?: { lints?: Lint[] } }).derived
  if (derived?.lints) return derived.lints
  return isStageBody(s.doc) ? s.doc.lints : EMPTY
}

export function LintList({ className, plain }: LintListProps) {
  const lints = useEditorStore(selectLints)
  const doc = useEditorStore((s) => s.doc)
  const select = useEditorStore((s) => s.select)
  const vocab = useVocab()
  const openPicker = useKindUi((s) => s.openPicker)
  const openNewKind = useKindUi((s) => s.openNewKind)
  const openEditKind = useKindUi((s) => s.openEditKind)

  const errors = lints.filter((l) => l.severity === 'error').length

  if (lints.length === 0) {
    return (
      <div className={cn('p-4 text-sm text-ink-2', className)}>
        <p>Nothing looks wrong with this picture.</p>
      </div>
    )
  }

  const kindFix = (lint: Lint) => {
    if (plain || !lint.ref) return null
    const domain = domainOfFeature(doc, lint.ref)
    if (!domain) return null
    const feature = featuresOf(doc, domain).find((f) => f.id === lint.ref)
    if (!feature) return null
    if (lint.code === 'W_UNKNOWN_KIND') {
      return (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => (select([feature.id]), openPicker(domain, { kind: 'set', ids: [feature.id] }))}>
            Choose what it is
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => (select([feature.id]), openNewKind(domain, { kind: 'set', ids: [feature.id] }, prettyId(feature.kind)))}>
            Add “{prettyId(feature.kind)}” to the list
          </Button>
        </div>
      )
    }
    if ((lint.code === 'W_KIND_INERT' || lint.code === 'W_FIELD_VALUE') && vocab.get(feature.kind, domain)) {
      return (
        <div className="mt-1.5">
          <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={() => openEditKind(domain, feature.kind)}>
            {lint.code === 'W_KIND_INERT' ? 'Say what it does' : 'Edit this kind'}
          </Button>
        </div>
      )
    }
    return null
  }

  return (
    <section aria-label="Things to look at" className={cn('p-3', className)}>
      <ul className="space-y-2">
        {lints.map((lint, i) => {
          const Icon = ICON[lint.severity]
          return (
            <li key={`${lint.code}:${lint.ref ?? i}:${i}`} className="flex gap-2 rounded-md border border-line bg-paper-2 p-2">
              <Icon className={cn('mt-0.5 size-4 shrink-0', TONE[lint.severity])} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink" title={plain ? undefined : lint.code}>
                  {lint.message}
                </p>
                {lint.fix_hint ? <p className="mt-0.5 text-xs text-ink-2">{lint.fix_hint}</p> : null}
                {kindFix(lint)}
              </div>
              {!plain && lint.ref ? (
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => select([lint.ref!])}>
                  Show me
                </Button>
              ) : null}
            </li>
          )
        })}
      </ul>

      {errors > 0 ? (
        <p className="mt-3 text-xs text-ink-2">
          These don’t stop you saving. {errors === 1 ? 'This one thing has' : `These ${errors} things have`} to be sorted out before this picture can be used in a story.
        </p>
      ) : null}
    </section>
  )
}
